"use client";

import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, FileText, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { InvoiceRow } from "./invoice-row";
import {
  SECTION_LABELS,
  STATUS_LABELS,
  type InvoiceSection,
  type InvoiceStatus,
  type MockInvoice,
} from "./types";

interface InvoicesPageClientProps {
  venueId: number;
  venueName: string;
  venueCurrency: string;
  userRole: string;
}

const SECTIONS: InvoiceSection[] = [
  "pending_manual",
  "pending_auto",
  "completed",
];

const STATUS_OPTIONS_BY_SECTION: Record<InvoiceSection, InvoiceStatus[]> = {
  pending_manual: ["flagged", "awaiting_review"],
  pending_auto: ["scheduled", "sending"],
  completed: ["sent", "failed"],
};

export default function InvoicesPageClient({
  venueId,
  venueName,
  venueCurrency: _venueCurrency,
  userRole: _userRole,
}: InvoicesPageClientProps) {
  const [invoices, setInvoices] = useState<MockInvoice[]>([]);
  const [autoInvoiceEnabled, setAutoInvoiceEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | InvoiceStatus>(
    "all",
  );
  const [activeTab, setActiveTab] = useState<InvoiceSection>("pending_manual");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/staff/venues/${venueId}/invoices`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || "Failed to load invoices");
        }
        const body = await res.json();
        if (!cancelled) {
          setInvoices(body.data ?? []);
          setAutoInvoiceEnabled(body.autoInvoiceEnabled ?? false);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load invoices",
          );
          setInvoices([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [venueId]);

  const counts = useMemo(
    () => ({
      pending_manual: invoices.filter((i) => i.section === "pending_manual")
        .length,
      pending_auto: invoices.filter((i) => i.section === "pending_auto").length,
      completed: invoices.filter((i) => i.section === "completed").length,
    }),
    [invoices],
  );

  const filteredBySection = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (inv.section !== activeTab) return false;
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      if (!q) return true;
      return (
        inv.booking_no.toLowerCase().includes(q) ||
        inv.customer_name.toLowerCase().includes(q) ||
        inv.end_customer_name?.toLowerCase().includes(q)
      );
    });
  }, [invoices, activeTab, statusFilter, search]);

  const handleTabChange = (value: string) => {
    setActiveTab(value as InvoiceSection);
    setStatusFilter("all");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Invoices</h1>
          <p className="text-gray-600 mt-2">
            Centralised invoice tasks for {venueName}. Flag bookings to hold
            them, review what is queued for auto-send, and track failures.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <div className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 mb-6">
                <TabsList className="h-auto rounded-none bg-transparent border-0 p-0 justify-start gap-6">
                  {SECTIONS.map((section) => (
                    <TabsTrigger
                      key={section}
                      value={section}
                      className="relative h-auto rounded-none border-0 border-b-2 border-transparent bg-transparent px-1 pb-3 pt-2 text-gray-500 shadow-none data-[state=active]:border-purple-600 data-[state=active]:bg-transparent data-[state=active]:text-purple-700 data-[state=active]:shadow-none hover:text-gray-700"
                    >
                      <span>{SECTION_LABELS[section]}</span>
                      <Badge
                        variant="secondary"
                        className="ml-2 bg-gray-100 text-gray-700"
                      >
                        {counts[section]}
                      </Badge>
                    </TabsTrigger>
                  ))}
                </TabsList>

                <div className="flex flex-wrap items-center gap-3 pb-3">
                  <div className="relative w-[260px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search booking or customer"
                      className="pl-9 h-9"
                    />
                  </div>
                  <div className="w-[180px]">
                    <Select
                      value={statusFilter}
                      onValueChange={(v) =>
                        setStatusFilter(v as "all" | InvoiceStatus)
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {STATUS_OPTIONS_BY_SECTION[activeTab].map((s) => (
                          <SelectItem key={s} value={s}>
                            {STATUS_LABELS[s]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {SECTIONS.map((section) => (
                <TabsContent key={section} value={section} className="mt-0">
                  {section === "pending_auto" && !autoInvoiceEnabled && (
                    // Auto-invoicing is off for this venue
                    // (venues.auto_invoice_enabled = false). Bookings still
                    // land in this tab when they meet the auto-eligibility
                    // criteria, but nothing fires for them until a super
                    // admin switches the venue on. Use Generate Invoice on
                    // the booking detail page to issue invoices manually.
                    <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                      <Info className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                      <div className="text-sm">
                        <p className="font-medium">
                          Auto-send is off for this venue.
                        </p>
                        <p className="text-amber-800 mt-0.5">
                          These bookings meet the auto-invoicing criteria but
                          nothing will fire until a super admin enables
                          automatic invoicing in the venue&apos;s accounting
                          settings. Use{" "}
                          <span className="font-medium">Generate Invoice</span>{" "}
                          on the booking detail page to issue them manually.
                        </p>
                      </div>
                    </div>
                  )}
                  {loading ? (
                    <div className="border rounded-lg overflow-hidden py-12 text-center text-gray-500">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-gray-400" />
                      Loading invoices…
                    </div>
                  ) : filteredBySection.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader className="bg-gray-50">
                          <TableRow className="border-b-2">
                            <TableHead className="text-gray-700 font-semibold px-6 py-4">
                              Booking
                            </TableHead>
                            <TableHead className="text-gray-700 font-semibold px-6 py-4">
                              Customer
                            </TableHead>
                            <TableHead className="text-gray-700 font-semibold px-6 py-4 text-right">
                              Amount
                            </TableHead>
                            <TableHead className="text-gray-700 font-semibold px-6 py-4">
                              Status
                            </TableHead>
                            <TableHead className="text-gray-700 font-semibold px-6 py-4">
                              Last action
                            </TableHead>
                            {section === "pending_manual" && (
                              <TableHead className="text-gray-700 font-semibold px-6 py-4">
                                Note
                              </TableHead>
                            )}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredBySection.map((invoice) => (
                            <InvoiceRow
                              key={`${invoice.section}-${invoice.id}`}
                              invoice={invoice}
                              venueId={venueId}
                              showNote={section === "pending_manual"}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <EmptyState
                      section={section}
                      hasFilters={!!search || statusFilter !== "all"}
                    />
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  section,
  hasFilters,
}: {
  section: InvoiceSection;
  hasFilters: boolean;
}) {
  const messages: Record<InvoiceSection, { title: string; body: string }> = {
    pending_manual: {
      title: "Nothing flagged",
      body: "Bookings flagged for manual review will show up here so you can edit them before invoicing.",
    },
    pending_auto: {
      title: "No invoices queued",
      body: "Completed bookings waiting for the auto-send window will show up here.",
    },
    completed: {
      title: "No invoices yet",
      body: "Once auto-send runs, sent and failed invoices will be listed here.",
    },
  };

  const { title, body } = messages[section];
  return (
    <div className="border rounded-lg overflow-hidden py-12">
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
          <FileText className="h-8 w-8 text-purple-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {hasFilters ? "No matches" : title}
        </h3>
        <p className="text-gray-600 max-w-md mx-auto">
          {hasFilters ? "Try clearing the search or status filter." : body}
        </p>
      </div>
    </div>
  );
}
